import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { unitService } from "@/services/api/cresivoxServer";
import { iUnit, REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

export const addUnit = createAsyncThunk<
  iGenericResponse<any | null> | null,
  {
    unitId?: string;
    name: string;
    // description: string;
  },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("addUnit"),
  // @ts-expect-error
  async (payload, { rejectWithValue }) => {
    try {
      const response = await unitService.addUnit(payload);
      if (response && response.error === null) {
        const { data } = response;
        return {
          data: {
            units: data,
            
            message: data.message || "Unit added successfully",
          },
        };
      } else {
        return rejectWithValue({
          message: response.error?.message || "An unknown error occurred",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);

export const getUnitList = createAsyncThunk<
  iGenericResponse<any | null> | null,
  void,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("unitList"),
  // @ts-expect-error
  async (arg, { rejectWithValue }) => {
    try {
      const response = await unitService.getUnitList();
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                httpStatusCode,
                data: data.data,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);

export const getUnitById = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { unitId: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("getUnitById"),
  // @ts-expect-error
  async ({ unitId }, { rejectWithValue }) => {
    try {
      const response = await unitService.getUnitById(unitId);
      if (response && response.error === null) {
        return {
          data: {
            data: response.data,
          },
        };
      } else {
        return rejectWithValue({
          message: response?.error?.message || "Failed to fetch unit data",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);

export const updateUnit = createAsyncThunk<
  iGenericResponse<any | null> | null,
  {
    unitId: string;
    name: string;
    description: string;
  },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("updateUnit"),
  // @ts-expect-error
  async (payload, { rejectWithValue }) => {
    try {
      const response = await unitService.updateUnit(payload);
      if (response && response.error === null) {
        return {
          data: {
            data: response.data,
           
            message: response.data.message || "Unit updated successfully",
          },
        };
      } else {
        return rejectWithValue({
          message: response?.error?.message || "Failed to update unit",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong while updating the unit",
      });
    }
  }
);

export const deleteUnit = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { unitId: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("deleteUnit"),
    // @ts-expect-error
  async ({ unitId }, { rejectWithValue }) => {
    try {
      const response = await unitService.deleteUnit(unitId);

      // ✅ Handle success properly
      //@ts-ignore
      if (response && (response.success || response.status === 200 || !response.error)) {
        return {
          data: {
            message:
              response?.message ||
              response?.data?.message ||
              "Unit deleted successfully",
          },
        };
      }

      // ❌ If backend returned an error object or invalid response
      return rejectWithValue({
        message:
          response?.error?.message ||
          response?.message ||
          "Failed to delete unit",
      });
    } catch (error: any) {
      console.error("❌ Delete thunk caught error:", error);
      return rejectWithValue({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while deleting the unit",
      });
    }
  }
);
